/**
 * Funnel Runtime Service
 *
 * HTTP client for NextStore funnel APIs (assign, evaluate-step, accept, decline).
 * Used by FunnelRuntimeProvider to manage session-based funnel experiences.
 * No auth required — these are public endpoints.
 */

import { FUNNELS_RUNTIME_CONFIG } from '../../../shared/config/constants'
import type {
  FunnelAssignRequest,
  FunnelAssignResponse,
  FunnelEvaluateStepRequest,
  FunnelEvaluateStepResponse,
  AcceptStepRequest,
  DeclineStepRequest,
  StepNavigationResponse,
} from './funnel-runtime.types'

async function funnelFetch<T>(path: string, body: Record<string, string | number | undefined | null | Record<string, string | undefined>>): Promise<T> {
  const { apiUrl, tenantId } = FUNNELS_RUNTIME_CONFIG
  const url = `${apiUrl}${path}?tenantId=${tenantId}`

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Funnel API error ${response.status}: ${text}`)
  }

  return response.json() as Promise<T>
}

export async function assignFunnel(req: FunnelAssignRequest): Promise<FunnelAssignResponse> {
  return funnelFetch<FunnelAssignResponse>('/v1/public/funnels/assign', {
    sessionId: req.sessionId,
    customerCurrency: req.customerCurrency,
    customerCountry: req.customerCountry,
    urlPath: req.urlPath,
  })
}

export async function evaluateStep(req: FunnelEvaluateStepRequest): Promise<FunnelEvaluateStepResponse> {
  return funnelFetch<FunnelEvaluateStepResponse>('/v1/public/funnels/evaluate-step', {
    sessionId: req.sessionId,
    funnelId: req.funnelId,
    pageContext: {
      pageType: req.pageContext.pageType,
      url: req.pageContext.url,
      productId: req.pageContext.productId,
      collectionId: req.pageContext.collectionId,
    },
  })
}

export async function acceptStep(req: AcceptStepRequest): Promise<StepNavigationResponse> {
  return funnelFetch<StepNavigationResponse>(
    `/v1/public/funnels/${req.funnelId}/steps/${req.stepId}/accept`,
    {
      sessionId: req.sessionId,
      action: req.action,
      productId: req.productId,
      variantId: req.variantId || '',
      quantity: req.quantity ?? 1,
    },
  )
}

export async function declineStep(req: DeclineStepRequest): Promise<StepNavigationResponse> {
  return funnelFetch<StepNavigationResponse>(
    `/v1/public/funnels/${req.funnelId}/steps/${req.stepId}/decline`,
    {
      sessionId: req.sessionId,
    },
  )
}
